import { useAuth } from "@clerk/clerk-react";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControlLabel,
  Grid,
  Link,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { blue, green, grey, orange, purple } from "@mui/material/colors";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useInView } from "react-intersection-observer";
import {
  BentoBrand,
  BentoBrandPartnership,
  COMPANY_TYPES,
  COMPENSATION_TYPES,
  CONTACT_TYPES,
  FOLLOWING_SIZES,
  FollowingSizeRange,
  LOCATIONS,
  TARGET_AGE_RANGES,
  TARGET_GENDERS,
  TargetAgeRange,
} from "schemas/dashboard";
import { OrganizationUser } from "schemas/dashboard";

import Alert from "components/Alert";
import LocationAutocomplete from "components/LocationAutocomplete";
import { UserLocation } from "components/LocationAutocomplete/schema";
import StyledPage from "components/Styled/Page";
import { fetcherAuth } from "utils/api";
import { formatNumber, generateInitials } from "utils/string";
import { useAlert } from "utils/useAlert";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Geopoint {
  country_code: string;
  latitude?: number;
  longitude?: number;
  label?: string;
  is_country?: boolean;
}

interface SearchParams {
  query?: string;
  category_ids?: number[];
  company_types?: string[];
  contact_types?: string[];
  locations?: string[];
  geopoints?: string[];
  min_instagram_followers?: number;
  max_instagram_followers?: number;
  compensation_types?: string[];
  target_genders?: string[];
  min_target_age?: number;
  max_target_age?: number;
}

interface BentoCategory {
  id: number;
  name: string;
  subcategories?: { id: number; name: string; displayName?: string }[];
}

interface AllCategory {
  category_id: number;
  name: string;
  is_main: boolean;
  relevance_score: number;
}

interface BrandContact {
  id: number;
  name: string;
  title: string;
  email: string;
  source: string;
  bestResponseRanking: number | null;
  bestResponseType: string | null;
  lastSentToAt: number | null;
}

interface SearchBrand extends BentoBrand {
  Score?: number;
  isLocalOnly?: boolean;
  allCategories?: AllCategory[];
  nicheness?: number;
  instagramModernnessScore?: number;
  languages?: string[];
  contacts?: BrandContact[];
  instagramPostingFrequency?: string;
  instagramMostRecentPost?: string;
  tiktokPostingFrequency?: string;
  tiktokMostRecentPost?: string;
}

type TierOption = "free" | "starter" | "pro" | "va";

const TIER_OPTIONS: { value: TierOption; label: string }[] = [
  { value: "free", label: "Free" },
  { value: "starter", label: "Starter" },
  { value: "pro", label: "Pro" },
  { value: "va", label: "VA" },
];

// ---------------------------------------------------------------------------
// SearchBrands Page
// ---------------------------------------------------------------------------

const SearchBrands = () => {
  const { getToken } = useAuth();
  const [message, severity, setAlert, closeAlert] = useAlert();

  // Organization user selection
  const [orgUsers, setOrgUsers] = useState<OrganizationUser[]>([]);
  const [orgLoading, setOrgLoading] = useState(false);
  const [orgInputValue, setOrgInputValue] = useState("");
  const [selectedOrgUser, setSelectedOrgUser] =
    useState<OrganizationUser | null>(null);

  // Admin search options
  const [useUpdatedVersion, setUseUpdatedVersion] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [boostTiers, setBoostTiers] = useState(false);
  const [tier, setTier] = useState<TierOption>("free");

  // Search parameters
  const [params, setParams] = useState<SearchParams>({
    company_types: ["all"],
    contact_types: ["all"],
    locations: ["all"],
  });
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Categories
  const [categories, setCategories] = useState<BentoCategory[]>([]);
  const [expandedCats, setExpandedCats] = useState<Set<number>>(new Set());

  // Results & infinite scroll
  const RESULTS_PER_PAGE = 20;
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [results, setResults] = useState<SearchBrand[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [esQuery, setEsQuery] = useState<any>(null);

  const REF_INDEX = 9;
  const [scrollRef, isScrollRefVisible] = useInView({
    rootMargin: "0px 0px",
  });

  // Fetch organization users
  const fetchOrgUsers = async (query?: string) => {
    setOrgLoading(true);
    try {
      const url = query
        ? `/api/admin/organization-users?query=${query}`
        : "/api/admin/organization-users";
      const res = await fetcherAuth(getToken, url, "GET");
      setOrgUsers(res.organizationUsers);
    } catch {
      // non-critical
    }
    setOrgLoading(false);
  };

  useEffect(() => {
    fetchOrgUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchOrgUsers(orgInputValue || undefined);
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgInputValue]);

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        "/api/admin/bento-categories",
        "GET",
      );
      setCategories(res.topLevelCategories || []);
    } catch {
      // non-critical
    }
  }, [getToken]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Update search params
  const updateParams = (patch: Partial<SearchParams>) => {
    setParams((prev) => ({ ...prev, ...patch }));
  };

  // Build summary chips for collapsed filter header
  const filterSummaryChips: string[] = [];
  if (params.query) filterSummaryChips.push(`"${params.query}"`);
  if (
    params.company_types &&
    !params.company_types.includes("all") &&
    params.company_types.length > 0
  )
    filterSummaryChips.push(`${params.company_types.length} co. types`);
  if (
    params.contact_types &&
    !params.contact_types.includes("all") &&
    params.contact_types.length > 0
  )
    filterSummaryChips.push(`${params.contact_types.length} contact types`);
  if (
    params.locations &&
    !params.locations.includes("all") &&
    params.locations.length > 0
  )
    filterSummaryChips.push(`${params.locations.length} locations`);
  if (params.geopoints && params.geopoints.length > 0)
    filterSummaryChips.push(`${params.geopoints.length} geopoints`);
  if (params.category_ids && params.category_ids.length > 0)
    filterSummaryChips.push(`${params.category_ids.length} categories`);
  if (params.compensation_types && params.compensation_types.length > 0)
    filterSummaryChips.push(`${params.compensation_types.length} comp.`);
  if (getFollowingSizeKey(params) !== "all")
    filterSummaryChips.push("IG filter");
  if (getTargetAgeKey(params) !== "all") filterSummaryChips.push("Age filter");
  if (params.target_genders && params.target_genders.length > 0)
    filterSummaryChips.push("Gender filter");

  // Build query params shared between initial search and load-more
  const buildQueryParams = (cursorValue?: string | null) => {
    const queryParams = new URLSearchParams();
    queryParams.set("size", String(RESULTS_PER_PAGE));
    queryParams.set("use_updated_version", String(useUpdatedVersion));
    queryParams.set("is_mobile", String(isMobile));
    queryParams.set("boost_tiers", String(boostTiers));

    if (cursorValue) {
      queryParams.set("cursor", cursorValue);
    }

    if (selectedOrgUser) {
      queryParams.set(
        "organization_id",
        String(selectedOrgUser.organizationId),
      );
    } else if (tier !== "free") {
      queryParams.set("tier", tier);
    }

    if (params.query) {
      queryParams.set("query", params.query);
    }

    if (
      params.company_types &&
      !params.company_types.includes("all") &&
      params.company_types.length > 0
    ) {
      for (const ct of params.company_types) {
        queryParams.append("company_types", ct);
      }
    }

    if (
      params.contact_types &&
      !params.contact_types.includes("all") &&
      params.contact_types.length > 0
    ) {
      for (const ct of params.contact_types) {
        queryParams.append("contact_types", ct);
      }
    }

    if (
      params.locations &&
      !params.locations.includes("all") &&
      params.locations.length > 0
    ) {
      for (const loc of params.locations) {
        queryParams.append("locations", loc);
      }
    }

    if (params.geopoints && params.geopoints.length > 0) {
      for (const gp of params.geopoints) {
        queryParams.append("geopoints", gp);
      }
    }

    if (params.category_ids && params.category_ids.length > 0) {
      for (const id of params.category_ids) {
        queryParams.append("category_ids", String(id));
      }
    }

    if (params.min_instagram_followers != null) {
      queryParams.set(
        "min_instagram_followers",
        String(params.min_instagram_followers),
      );
    }
    if (params.max_instagram_followers != null) {
      queryParams.set(
        "max_instagram_followers",
        String(params.max_instagram_followers),
      );
    }

    if (params.compensation_types && params.compensation_types.length > 0) {
      for (const ct of params.compensation_types) {
        queryParams.append("compensation_types", ct);
      }
    }

    if (params.target_genders && params.target_genders.length > 0) {
      for (const g of params.target_genders) {
        queryParams.append("target_genders", g);
      }
    }

    if (params.min_target_age != null) {
      queryParams.set("min_target_age", String(params.min_target_age));
    }
    if (params.max_target_age != null) {
      queryParams.set("max_target_age", String(params.max_target_age));
    }

    return queryParams;
  };

  // Initial search (resets results)
  const handleSearch = async () => {
    setLoading(true);
    setResults([]);
    setTotalResults(0);
    setCursor(null);
    setHasMore(false);
    setEsQuery(null);

    try {
      const queryParams = buildQueryParams();
      const res = await fetcherAuth(
        getToken,
        `/api/admin/bento-brands/search?${queryParams.toString()}`,
        "GET",
      );
      const brands = res.bentoBrands || [];
      setResults(brands);
      setTotalResults(res.totalResults || 0);
      setCursor(res.cursor ? JSON.stringify(res.cursor) : null);
      setHasMore(brands.length >= RESULTS_PER_PAGE);
      if (res.elasticsearchQuery) {
        setEsQuery(res.elasticsearchQuery);
      }
    } catch (error: any) {
      setAlert(error?.message || "Search failed", "warning");
    } finally {
      setLoading(false);
    }
  };

  // Load more results (appends to existing)
  const fetchMore = async () => {
    if (loadingMore || !hasMore || !cursor) return;
    setLoadingMore(true);

    try {
      const queryParams = buildQueryParams(cursor);
      const res = await fetcherAuth(
        getToken,
        `/api/admin/bento-brands/search?${queryParams.toString()}`,
        "GET",
      );
      const brands = res.bentoBrands || [];
      setResults((prev) => [...prev, ...brands]);
      setCursor(res.cursor ? JSON.stringify(res.cursor) : null);
      setHasMore(brands.length >= RESULTS_PER_PAGE);
    } catch (error: any) {
      setAlert(error?.message || "Load more failed", "warning");
    } finally {
      setLoadingMore(false);
    }
  };

  // Trigger load-more when sentinel becomes visible
  useEffect(() => {
    if (isScrollRefVisible && !loading && !loadingMore && hasMore) {
      fetchMore();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScrollRefVisible, loading, loadingMore, hasMore]);

  // Category helpers
  const selectedCatIds = new Set(params.category_ids || []);

  const toggleCategory = (catId: number) => {
    const current = params.category_ids || [];
    if (current.includes(catId)) {
      updateParams({ category_ids: current.filter((id) => id !== catId) });
    } else {
      updateParams({ category_ids: [...current, catId] });
    }
  };

  const toggleExpandCat = (catId: number) => {
    setExpandedCats((prev) => {
      const next = new Set(prev);
      if (next.has(catId)) next.delete(catId);
      else next.add(catId);
      return next;
    });
  };

  return (
    <StyledPage
      content={
        <Box sx={{ p: 2, maxWidth: 1400 }}>
          <Typography variant="h5" sx={{ mb: 2 }}>
            Search Brands
          </Typography>

          {/* ── Toolbar ── */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1.5,
              mb: 1.5,
              p: 1.5,
              bgcolor: grey[50],
              borderRadius: 1.5,
            }}
          >
            <Button
              variant="contained"
              size="small"
              onClick={handleSearch}
              disabled={loading}
            >
              {loading ? "Searching..." : "Search"}
            </Button>

            <Divider orientation="vertical" flexItem />

            {/* Organization user selector */}
            <Autocomplete
              options={orgUsers}
              loading={orgLoading}
              sx={{ width: 340 }}
              size="small"
              getOptionLabel={(option) =>
                `${option.name} - ${option.email} | ${option.organizationId}`
              }
              value={selectedOrgUser}
              onChange={(_event: any, newValue: OrganizationUser | null) => {
                setSelectedOrgUser(newValue);
              }}
              onInputChange={(_event: any, newInputValue: string) => {
                setOrgInputValue(newInputValue);
              }}
              renderInput={(inputParams) => (
                <TextField
                  {...inputParams}
                  placeholder="Organization (optional)"
                  size="small"
                  InputProps={{
                    ...inputParams.InputProps,
                    sx: { fontSize: 12 },
                    endAdornment: (
                      <React.Fragment>
                        {orgLoading ? (
                          <CircularProgress color="inherit" size={16} />
                        ) : null}
                        {inputParams.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
            />

            <Divider orientation="vertical" flexItem />

            {/* Tier */}
            <CompactSelect
              label={selectedOrgUser ? "Tier (ignored)" : "Tier"}
              value={tier}
              onChange={(v) => setTier(v as TierOption)}
              options={TIER_OPTIONS}
              disabled={!!selectedOrgUser}
            />

            {/* Updated version toggle */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={useUpdatedVersion}
                  onChange={(e) => setUseUpdatedVersion(e.target.checked)}
                  size="small"
                  sx={{ p: 0.25 }}
                />
              }
              label={<Typography sx={{ fontSize: 12 }}>Updated</Typography>}
              sx={{ m: 0 }}
            />

            {/* Mobile toggle */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={isMobile}
                  onChange={(e) => setIsMobile(e.target.checked)}
                  size="small"
                  sx={{ p: 0.25 }}
                />
              }
              label={<Typography sx={{ fontSize: 12 }}>Mobile</Typography>}
              sx={{ m: 0 }}
            />

            {/* Boost tiers toggle */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={boostTiers}
                  onChange={(e) => setBoostTiers(e.target.checked)}
                  size="small"
                  sx={{ p: 0.25 }}
                />
              }
              label={<Typography sx={{ fontSize: 12 }}>Boost Tiers</Typography>}
              sx={{ m: 0 }}
            />

            {totalResults > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ ml: "auto", fontSize: 12 }}
              >
                {results.length} / {totalResults} results
              </Typography>
            )}

            {esQuery && (
              <Button
                size="small"
                variant="outlined"
                sx={{ fontSize: 11, textTransform: "none", py: 0.25 }}
                onClick={() => {
                  navigator.clipboard.writeText(
                    JSON.stringify(esQuery, null, 2),
                  );
                  setAlert("ES query copied to clipboard", "success");
                }}
              >
                Copy ES Query
              </Button>
            )}
          </Box>

          {/* ── Search Parameters (collapsible) ── */}
          <Box
            sx={{
              mb: 1.5,
              border: "1px solid",
              borderColor: filtersExpanded ? grey[400] : grey[200],
              borderRadius: 1.5,
              overflow: "hidden",
              transition: "border-color 0.15s",
            }}
          >
            {/* Collapsed header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.5,
                py: 0.75,
                cursor: "pointer",
                bgcolor: filtersExpanded ? grey[50] : "transparent",
                "&:hover": { bgcolor: grey[50] },
              }}
              onClick={() => setFiltersExpanded((v) => !v)}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, flexShrink: 0, fontSize: 12 }}
              >
                Search Parameters
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.5,
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                {filterSummaryChips.map((label) => (
                  <Chip
                    key={label}
                    label={label}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: 10, height: 20 }}
                  />
                ))}
                {filterSummaryChips.length === 0 && (
                  <Typography variant="caption" color="text.disabled">
                    no filters
                  </Typography>
                )}
              </Box>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ flexShrink: 0 }}
              >
                {filtersExpanded ? "\u25BE" : "\u25B8"}
              </Typography>
            </Box>

            {/* Expanded editor */}
            <Collapse in={filtersExpanded}>
              <Box sx={{ px: 1.5, pb: 1.5, pt: 1 }}>
                {/* Query */}
                <TextField
                  placeholder="Search query"
                  value={params.query || ""}
                  onChange={(e) =>
                    updateParams({ query: e.target.value || undefined })
                  }
                  size="small"
                  fullWidth
                  sx={{ mb: 1 }}
                  InputProps={{ sx: { fontSize: 13 } }}
                />

                {/* Filter grid */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(140px, 1fr))",
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <CompactSelect
                    label="Company"
                    multiple
                    value={params.company_types || ["all"]}
                    onChange={(v) =>
                      updateParams({
                        company_types: handleMultiSelectChange(
                          params.company_types,
                          v as string[],
                        ),
                      })
                    }
                    options={COMPANY_TYPES.map((ct) => ({
                      value: ct.key,
                      label: ct.name,
                    }))}
                    renderValue={(sel) =>
                      (sel as string[]).includes("all")
                        ? "All"
                        : `${(sel as string[]).length} sel.`
                    }
                  />

                  <CompactSelect
                    label="Contact"
                    multiple
                    value={params.contact_types || ["all"]}
                    onChange={(v) =>
                      updateParams({
                        contact_types: handleMultiSelectChange(
                          params.contact_types,
                          v as string[],
                        ),
                      })
                    }
                    options={CONTACT_TYPES.map((ct) => ({
                      value: ct.key,
                      label: ct.name,
                    }))}
                    renderValue={(sel) =>
                      (sel as string[]).includes("all")
                        ? "All"
                        : `${(sel as string[]).length} sel.`
                    }
                  />

                  <CompactSelect
                    label="Location"
                    multiple
                    value={params.locations || ["all"]}
                    onChange={(v) =>
                      updateParams({
                        locations: handleMultiSelectChange(
                          params.locations,
                          v as string[],
                        ),
                      })
                    }
                    options={LOCATIONS.map((loc) => ({
                      value: loc.key,
                      label: loc.name,
                    }))}
                    renderValue={(sel) =>
                      (sel as string[]).includes("all")
                        ? "All"
                        : `${(sel as string[]).length} sel.`
                    }
                  />

                  <CompactSelect
                    label="IG Following"
                    value={getFollowingSizeKey(params)}
                    onChange={(v) => {
                      const key = v as string;
                      if (key === "all") {
                        updateParams({
                          min_instagram_followers: undefined,
                          max_instagram_followers: undefined,
                        });
                      } else {
                        const range = FollowingSizeRange[key];
                        updateParams({
                          min_instagram_followers: range.min,
                          max_instagram_followers: range.max,
                        });
                      }
                    }}
                    options={FOLLOWING_SIZES.map((fs) => ({
                      value: fs.key,
                      label: fs.name,
                    }))}
                  />

                  <CompactSelect
                    label="Compensation"
                    multiple
                    value={params.compensation_types || []}
                    onChange={(v) =>
                      updateParams({
                        compensation_types:
                          (v as string[]).length === 0
                            ? undefined
                            : (v as string[]),
                      })
                    }
                    options={COMPENSATION_TYPES.map((ct) => ({
                      value: ct.key,
                      label: ct.name,
                    }))}
                    renderValue={(sel) =>
                      (sel as string[]).length === 0
                        ? "Any"
                        : `${(sel as string[]).length} sel.`
                    }
                    displayEmpty
                  />

                  <CompactSelect
                    label="Gender"
                    multiple
                    value={params.target_genders || []}
                    onChange={(v) =>
                      updateParams({
                        target_genders:
                          (v as string[]).length === 0
                            ? undefined
                            : (v as string[]),
                      })
                    }
                    options={TARGET_GENDERS.filter((g) => g.key !== "all").map(
                      (g) => ({
                        value: g.key,
                        label: g.name,
                      }),
                    )}
                    renderValue={(sel) =>
                      (sel as string[]).length === 0
                        ? "Any"
                        : `${(sel as string[]).length} sel.`
                    }
                    displayEmpty
                  />

                  <CompactSelect
                    label="Age"
                    value={getTargetAgeKey(params)}
                    onChange={(v) => {
                      const key = v as string;
                      if (key === "all") {
                        updateParams({
                          min_target_age: undefined,
                          max_target_age: undefined,
                        });
                      } else {
                        const range = TargetAgeRange[key];
                        updateParams({
                          min_target_age: range.min,
                          max_target_age: range.max,
                        });
                      }
                    }}
                    options={TARGET_AGE_RANGES.map((age) => ({
                      value: age.key,
                      label: age.name,
                    }))}
                  />
                </Box>

                {/* Geopoints */}
                <GeopointPicker
                  geopoints={params.geopoints || []}
                  onChange={(geopoints) =>
                    updateParams({
                      geopoints: geopoints.length > 0 ? geopoints : undefined,
                    })
                  }
                />

                {/* Categories */}
                {categories.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontWeight: 600,
                        display: "block",
                        mb: 0.5,
                      }}
                    >
                      Categories ({selectedCatIds.size})
                    </Typography>
                    <Box
                      sx={{
                        maxHeight: 200,
                        overflow: "auto",
                        border: "1px solid",
                        borderColor: grey[200],
                        borderRadius: 1,
                        p: 0.75,
                      }}
                    >
                      {categories.map((topCat) => (
                        <Box key={topCat.id}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              cursor: "pointer",
                              "&:hover": { bgcolor: grey[50] },
                              borderRadius: 0.5,
                              px: 0.5,
                              py: 0.125,
                            }}
                            onClick={() => toggleExpandCat(topCat.id)}
                          >
                            <Typography
                              sx={{
                                fontWeight: 600,
                                flex: 1,
                                fontSize: 12,
                              }}
                            >
                              {topCat.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: 10 }}
                            >
                              {expandedCats.has(topCat.id) ? "\u2212" : "+"}
                            </Typography>
                          </Box>
                          {expandedCats.has(topCat.id) &&
                            topCat.subcategories &&
                            topCat.subcategories.length > 0 && (
                              <Box
                                sx={{
                                  pl: 1.5,
                                  display: "flex",
                                  flexWrap: "wrap",
                                }}
                              >
                                {topCat.subcategories.map((sub) => (
                                  <FormControlLabel
                                    key={sub.id}
                                    control={
                                      <Checkbox
                                        checked={selectedCatIds.has(sub.id)}
                                        onChange={() => toggleCategory(sub.id)}
                                        size="small"
                                        sx={{ p: 0.25 }}
                                      />
                                    }
                                    label={
                                      <Typography sx={{ fontSize: 11 }}>
                                        {sub.displayName || sub.name}
                                      </Typography>
                                    }
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      mr: 1.5,
                                      my: 0,
                                      minWidth: 0,
                                    }}
                                  />
                                ))}
                              </Box>
                            )}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </Collapse>
          </Box>

          {/* ── Results ── */}
          {loading ? (
            <Grid container justifyContent="center" sx={{ py: 4 }}>
              <CircularProgress />
            </Grid>
          ) : results.length > 0 ? (
            <>
              <Grid container spacing={2}>
                {results.map((brand, idx) => (
                  <Grid
                    item
                    xs={12}
                    md={6}
                    key={brand.id}
                    ref={
                      (
                        results.length > REF_INDEX
                          ? idx === results.length - REF_INDEX
                          : idx === results.length - 1
                      )
                        ? scrollRef
                        : undefined
                    }
                  >
                    <BrandResultCard brand={brand} />
                  </Grid>
                ))}
              </Grid>
              {loadingMore && (
                <Grid container justifyContent="center" sx={{ py: 3 }}>
                  <CircularProgress size={28} />
                </Grid>
              )}
              {!hasMore && results.length > 0 && (
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ display: "block", textAlign: "center", py: 2 }}
                >
                  All {results.length} of {totalResults} results loaded
                </Typography>
              )}
            </>
          ) : null}

          <Alert
            message={message}
            severity={severity}
            closeAlert={closeAlert}
          />
        </Box>
      }
    />
  );
};

export default SearchBrands;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function handleMultiSelectChange(
  current: string[] | undefined,
  newValues: string[],
): string[] {
  if (newValues.includes("all") && !current?.includes("all")) {
    return ["all"];
  }
  const filtered = newValues.filter((v) => v !== "all");
  return filtered.length === 0 ? ["all"] : filtered;
}

function getFollowingSizeKey(params: SearchParams): string {
  if (
    params.min_instagram_followers == null &&
    params.max_instagram_followers == null
  )
    return "all";
  for (const [key, range] of Object.entries(FollowingSizeRange)) {
    if (
      range.min === params.min_instagram_followers &&
      range.max === params.max_instagram_followers
    )
      return key;
  }
  return "all";
}

function getTargetAgeKey(params: SearchParams): string {
  if (params.min_target_age == null && params.max_target_age == null)
    return "all";
  for (const [key, range] of Object.entries(TargetAgeRange)) {
    if (
      range.min === params.min_target_age &&
      range.max === params.max_target_age
    )
      return key;
  }
  return "all";
}

// ---------------------------------------------------------------------------
// CompactSelect
// ---------------------------------------------------------------------------

function CompactSelect({
  label,
  multiple,
  value,
  onChange,
  options,
  renderValue,
  displayEmpty,
  disabled,
}: {
  label: string;
  multiple?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (value: any) => void;
  options: { value: string; label: string }[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderValue?: (value: any) => React.ReactNode;
  displayEmpty?: boolean;
  disabled?: boolean;
}) {
  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontSize: 10, display: "block", lineHeight: 1.2 }}
      >
        {label}
      </Typography>
      <Select
        multiple={multiple}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        size="small"
        fullWidth
        displayEmpty={displayEmpty}
        renderValue={renderValue}
        disabled={disabled}
        sx={{ "& .MuiSelect-select": { py: 0.5, fontSize: 12 } }}
      >
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// GeopointPicker
// ---------------------------------------------------------------------------

function GeopointPicker({
  geopoints,
  onChange,
}: {
  geopoints: string[];
  onChange: (geopoints: string[]) => void;
}) {
  const geopointsRef = useRef<string[]>(geopoints);
  useEffect(() => {
    geopointsRef.current = geopoints;
  }, [geopoints]);

  const parsed = useMemo(() => {
    return geopoints
      .map((g) => {
        try {
          return JSON.parse(g) as Geopoint;
        } catch {
          return null;
        }
      })
      .filter(Boolean) as Geopoint[];
  }, [geopoints]);

  const handleLocationChange = (userLocation: UserLocation) => {
    const current = geopointsRef.current || [];
    const currentParsed: Geopoint[] = current
      .map((g) => {
        try {
          return JSON.parse(g) as Geopoint;
        } catch {
          return null;
        }
      })
      .filter(Boolean) as Geopoint[];

    const addressComponents = userLocation?.address_components;

    let city = addressComponents?.find(
      (x) =>
        x?.types?.includes("administrative_area_level_3") ||
        x?.types?.includes("locality"),
    )?.longText;

    const region = addressComponents?.find((x) =>
      x?.types?.includes("administrative_area_level_1"),
    )?.longText;

    let isCountry = false;
    if (!city && !region) {
      city = addressComponents?.[0]?.longText;
      isCountry = true;
    }

    if (
      userLocation?.country_code &&
      userLocation.latitude &&
      userLocation.longitude
    ) {
      const geopoint: Geopoint = {
        country_code: userLocation.country_code,
        latitude: userLocation.latitude,
        longitude: userLocation.longitude,
        is_country: isCountry,
        label: city || region,
      };

      const exists = currentParsed.some(
        (g) =>
          g.latitude === geopoint.latitude &&
          g.longitude === geopoint.longitude,
      );
      if (!exists) {
        onChange([...current, JSON.stringify(geopoint)]);
      }
    }
  };

  const removeGeopoint = (index: number) => {
    const current = geopointsRef.current || [];
    onChange(current.filter((_, i) => i !== index));
  };

  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontSize: 10, display: "block", lineHeight: 1.2 }}
      >
        Cities / Countries
      </Typography>
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          border: "1px solid",
          borderColor: grey[300],
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <LocationAutocomplete
          handleLocationChange={handleLocationChange}
          customStyling={{
            border: "none",
            borderRadius: 1,
            height: 28,
            py: 0.25,
            px: 1,
            width: "100%",
            fontSize: 12,
            "&:hover, &:focus": { outline: "none" },
          }}
          placeholderText="Add city or country..."
        />
      </Box>

      {parsed.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
          {parsed.map((gp, i) => (
            <Chip
              key={i}
              label={
                gp.label ? `${gp.label} (${gp.country_code})` : gp.country_code
              }
              size="small"
              onDelete={() => removeGeopoint(i)}
              sx={{ fontSize: 11, height: 22 }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// StatValue
// ---------------------------------------------------------------------------

function StatValue({
  label,
  value,
  color,
}: {
  label: string;
  value: React.ReactNode;
  color?: string;
}) {
  return (
    <Box sx={{ textAlign: "center", minWidth: 48 }}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          lineHeight: 1.1,
          color: color || "text.primary",
        }}
      >
        {value}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontSize: 10, lineHeight: 1.2 }}
      >
        {label}
      </Typography>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// BrandResultCard
// ---------------------------------------------------------------------------

function BrandResultCard({ brand }: { brand: SearchBrand }) {
  const [expanded, setExpanded] = useState(false);
  const partnerships = brand.partnerships || [];
  const contacts = brand.contacts || [];
  const esScore = brand.Score;
  const recentPartnerships = partnerships.slice(0, 5);
  const remainingPartnerships = partnerships.length - 5;

  const hasPostingData =
    brand.instagramPostingFrequency ||
    brand.instagramMostRecentPost ||
    brand.tiktokPostingFrequency ||
    brand.tiktokMostRecentPost;

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: grey[200],
        borderRadius: 2,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        transition: "box-shadow 0.15s",
        "&:hover": { boxShadow: 2 },
      }}
    >
      {/* ── Always visible ── */}

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          pt: 1.5,
          pb: 0.75,
          cursor: "pointer",
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        {brand.logoUrl ? (
          <Box
            component="img"
            src={brand.logoUrl}
            sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              border: "1px solid",
              borderColor: grey[200],
              objectFit: "contain",
              flexShrink: 0,
            }}
          />
        ) : (
          <Avatar
            sx={{
              bgcolor: green[700],
              width: 32,
              height: 32,
              fontSize: 13,
              flexShrink: 0,
            }}
          >
            {generateInitials(brand.brandName)}
          </Avatar>
        )}

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: 13 }}
            noWrap
          >
            {brand.brandName}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              flexWrap: "wrap",
            }}
          >
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ fontSize: 10 }}
            >
              #{brand.id}
            </Typography>
            {brand.instagramUsername && (
              <Link
                href={`https://instagram.com/${brand.instagramUsername}`}
                target="_blank"
                sx={{ fontSize: 10 }}
                onClick={(e) => e.stopPropagation()}
              >
                @{brand.instagramUsername}
              </Link>
            )}
            {brand.tiktokUsername && (
              <Link
                href={`https://tiktok.com/@${brand.tiktokUsername}`}
                target="_blank"
                sx={{ fontSize: 10 }}
                onClick={(e) => e.stopPropagation()}
              >
                TT: @{brand.tiktokUsername}
              </Link>
            )}
            {brand.website && (
              <Link
                href={`https://${brand.website}`}
                target="_blank"
                sx={{ fontSize: 10 }}
                onClick={(e) => e.stopPropagation()}
              >
                {brand.website.split("/")[0]}
              </Link>
            )}
          </Box>
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ flexShrink: 0, fontSize: 12 }}
        >
          {expanded ? "\u25BE" : "\u25B8"}
        </Typography>
      </Box>

      {/* Stat Row */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-around",
          px: 1.5,
          py: 0.75,
          borderTop: "1px solid",
          borderColor: grey[100],
          bgcolor: grey[50],
        }}
      >
        <StatValue
          label="ES Score"
          value={esScore != null ? Math.round(esScore) : "-"}
          color={blue[700]}
        />
        <StatValue
          label="IG Followers"
          value={
            brand.instagramFollowerCount != null
              ? formatNumber(brand.instagramFollowerCount)
              : "-"
          }
        />
        <StatValue label="Contacts" value={contacts.length} />
        <StatValue label="Partnerships" value={partnerships.length} />
        <StatValue
          label="Emailed"
          value={brand.contactedRecentlyAmount ?? "-"}
          color={brand.contactedRecentlyAmount ? orange[800] : undefined}
        />
        <StatValue
          label="Tier"
          value={
            brand.tier === "tier_one"
              ? "1"
              : brand.tier === "tier_two"
                ? "2"
                : "-"
          }
          color={
            brand.tier === "tier_one"
              ? green[700]
              : brand.tier === "tier_two"
                ? blue[700]
                : undefined
          }
        />
        <StatValue
          label="Nicheness"
          value={brand.nicheness != null ? brand.nicheness : "-"}
        />
      </Box>

      {/* Categories */}
      {brand.allCategories && brand.allCategories.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5,
            px: 2,
            py: 0.75,
          }}
        >
          {[...brand.allCategories]
            .sort((a, b) => b.relevance_score - a.relevance_score)
            .map((cat) => (
              <Chip
                key={cat.category_id}
                label={`${cat.name} (${cat.relevance_score})`}
                size="small"
                sx={{
                  fontSize: 10,
                  height: 20,
                  bgcolor:
                    cat.relevance_score >= 8
                      ? green[50]
                      : cat.relevance_score >= 5
                        ? orange[50]
                        : grey[100],
                  border: `1px solid ${
                    cat.relevance_score >= 8
                      ? green[300]
                      : cat.relevance_score >= 5
                        ? orange[300]
                        : grey[300]
                  }`,
                  fontWeight: cat.is_main ? 700 : 400,
                }}
              />
            ))}
        </Box>
      )}

      {/* Posting Frequency + Location row */}
      {(hasPostingData ||
        brand.isInternational ||
        brand.isLocalOnly ||
        (brand.locationNames && brand.locationNames.length > 0) ||
        (brand.countries && brand.countries.length > 0)) && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 0.5,
            px: 2,
            py: 0.5,
            borderTop: "1px solid",
            borderColor: grey[100],
            bgcolor: grey[50],
          }}
        >
          {/* IG posting */}
          {brand.instagramPostingFrequency && (
            <Chip
              label={`IG: ${brand.instagramPostingFrequency.replace(/_/g, " ")}`}
              size="small"
              variant="outlined"
              sx={{ fontSize: 10, height: 20 }}
            />
          )}
          {brand.instagramMostRecentPost && (
            <Chip
              label={`IG post: ${brand.instagramMostRecentPost.replace(/_/g, " ")}`}
              size="small"
              variant="outlined"
              sx={{ fontSize: 10, height: 20 }}
            />
          )}
          {/* TT posting */}
          {brand.tiktokPostingFrequency && (
            <Chip
              label={`TT: ${brand.tiktokPostingFrequency.replace(/_/g, " ")}`}
              size="small"
              variant="outlined"
              sx={{ fontSize: 10, height: 20 }}
            />
          )}
          {brand.tiktokMostRecentPost && (
            <Chip
              label={`TT post: ${brand.tiktokMostRecentPost.replace(/_/g, " ")}`}
              size="small"
              variant="outlined"
              sx={{ fontSize: 10, height: 20 }}
            />
          )}

          {/* Divider between posting and location if both present */}
          {hasPostingData &&
            (brand.isInternational ||
              brand.isLocalOnly ||
              (brand.locationNames && brand.locationNames.length > 0) ||
              (brand.countries && brand.countries.length > 0)) && (
              <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
            )}

          {/* Location chips */}
          {brand.isInternational && (
            <Chip
              label="International"
              size="small"
              sx={{
                fontSize: 10,
                height: 20,
                bgcolor: blue[50],
                color: blue[800],
                borderColor: blue[200],
              }}
              variant="outlined"
            />
          )}
          {brand.isLocalOnly && (
            <Chip
              label="Local Only"
              size="small"
              sx={{
                fontSize: 10,
                height: 20,
                bgcolor: orange[50],
                color: orange[900],
                borderColor: orange[200],
              }}
              variant="outlined"
            />
          )}
          {brand.locationNames && brand.locationNames.length > 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: 10 }}
            >
              {brand.locationNames.join(", ")}
            </Typography>
          )}
          {(!brand.locationNames || brand.locationNames.length === 0) &&
            brand.countries &&
            brand.countries.length > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: 10 }}
              >
                {brand.countries.join(", ")}
              </Typography>
            )}
        </Box>
      )}

      {/* ── Expandable details ── */}
      <Collapse in={expanded}>
        {/* Brand Flags */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5,
            px: 2,
            py: 0.75,
            borderTop: "1px solid",
            borderColor: grey[100],
          }}
        >
          {brand.worksWithUgc && (
            <Chip
              label="UGC"
              size="small"
              color="success"
              variant="outlined"
              sx={{ fontSize: 10, height: 20 }}
            />
          )}
          {brand.worksWithInfluencer && (
            <Chip
              label="Influencer"
              size="small"
              color="success"
              variant="outlined"
              sx={{ fontSize: 10, height: 20 }}
            />
          )}
          {brand.hasValidatedPartnership && (
            <Chip
              label="Validated"
              size="small"
              color="info"
              variant="outlined"
              sx={{ fontSize: 10, height: 20 }}
            />
          )}
          {brand.compensationTypes?.map((ct) => (
            <Chip
              key={ct}
              label={ct.replace(/_/g, " ")}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontSize: 10, height: 20, textTransform: "capitalize" }}
            />
          ))}
          {brand.instagramModernnessScore != null && (
            <Chip
              label={`IG Modern: ${brand.instagramModernnessScore}`}
              size="small"
              variant="outlined"
              sx={{ fontSize: 10, height: 20 }}
            />
          )}
        </Box>

        {/* Languages */}
        {brand.languages && brand.languages.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
              px: 2,
              pb: 0.75,
            }}
          >
            {brand.languages.map((lang) => (
              <Chip
                key={lang}
                label={lang}
                size="small"
                variant="outlined"
                sx={{
                  fontSize: 10,
                  height: 20,
                  borderColor: purple[200],
                  color: purple[700],
                }}
              />
            ))}
          </Box>
        )}

        {/* Metadata Tags */}
        {brand.metadata && brand.metadata.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
              px: 2,
              pb: 0.75,
            }}
          >
            {brand.metadata.map((tag, i) => (
              <Chip
                key={`m-${i}`}
                label={tag.value}
                size="small"
                sx={{
                  fontSize: 10,
                  height: 20,
                  bgcolor: "white",
                  border: `0.5px solid ${tag.type === "location" ? grey[900] : grey[500]}`,
                  fontWeight: tag.type === "location" ? 600 : 400,
                }}
              />
            ))}
          </Box>
        )}

        {/* About */}
        {brand.aboutSummary && (
          <Box sx={{ px: 2, py: 0.75 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: 11 }}
            >
              {brand.aboutSummary}
            </Typography>
          </Box>
        )}

        {/* Contacts Table */}
        <Box sx={{ px: 2, py: 0.75 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 600, display: "block", mb: 0.5, fontSize: 11 }}
          >
            Contacts ({contacts.length})
          </Typography>
          {contacts.length > 0 ? (
            <TableContainer
              sx={{
                border: "1px solid",
                borderColor: grey[200],
                borderRadius: 1,
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: grey[50] }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: 11, py: 0.5 }}>
                      Name
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 11, py: 0.5 }}>
                      Email
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 11, py: 0.5 }}>
                      Title
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 11, py: 0.5 }}>
                      Source
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 11, py: 0.5 }}>
                      Response
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 11, py: 0.5 }}>
                      Last Sent
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contacts.map((contact) => (
                    <TableRow key={contact.id}>
                      <TableCell sx={{ fontSize: 11, py: 0.5 }}>
                        {contact.name || "-"}
                      </TableCell>
                      <TableCell sx={{ fontSize: 11, py: 0.5 }}>
                        {contact.email || "-"}
                      </TableCell>
                      <TableCell sx={{ fontSize: 11, py: 0.5 }}>
                        {contact.title || "-"}
                      </TableCell>
                      <TableCell sx={{ fontSize: 11, py: 0.5 }}>
                        {contact.source || "-"}
                      </TableCell>
                      <TableCell sx={{ py: 0.5 }}>
                        {contact.bestResponseType ? (
                          <Chip
                            label={contact.bestResponseType}
                            size="small"
                            color={getResponseColor(contact.bestResponseType)}
                            variant="outlined"
                            sx={{ fontSize: 9, height: 18 }}
                          />
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: 11, py: 0.5 }}>
                        {contact.lastSentToAt
                          ? new Date(
                              contact.lastSentToAt * 1000,
                            ).toLocaleDateString()
                          : "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: 11 }}
            >
              No contacts.
            </Typography>
          )}
        </Box>

        {/* Partnerships */}
        {partnerships.length > 0 && (
          <Box sx={{ px: 2, py: 0.75, pb: 1.5 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600, display: "block", mb: 0.5, fontSize: 11 }}
            >
              Partnerships ({partnerships.length})
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {recentPartnerships.map((p, i) => (
                <PartnershipChip key={i} partnership={p} />
              ))}
              {remainingPartnerships > 0 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    alignSelf: "center",
                    fontSize: 11,
                    fontStyle: "italic",
                  }}
                >
                  +{remainingPartnerships} more
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Collapse>
    </Box>
  );
}

function getResponseColor(
  responseType: string,
): "success" | "warning" | "error" | "default" {
  const lower = responseType.toLowerCase();
  if (lower === "positive" || lower === "interested") return "success";
  if (lower === "negative" || lower === "not_interested") return "error";
  if (lower === "maybe" || lower === "follow_up") return "warning";
  return "default";
}

// ---------------------------------------------------------------------------
// PartnershipChip
// ---------------------------------------------------------------------------

function PartnershipChip({
  partnership,
}: {
  partnership: BentoBrandPartnership;
}) {
  const compTypes = Array.isArray(partnership.compensation_types)
    ? partnership.compensation_types.join(", ")
    : partnership.compensation_types || "";
  const country = partnership.country || partnership.organization_country_code;
  const status = partnership.status;

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: grey[200],
        borderRadius: 1,
        p: 1,
        fontSize: 12,
        display: "flex",
        flexDirection: "column",
        gap: 0.25,
        bgcolor: grey[50],
      }}
    >
      <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
        <Chip
          label={status}
          size="small"
          color={
            status === "active"
              ? "success"
              : status === "completed"
                ? "info"
                : "default"
          }
          variant="outlined"
          sx={{ fontSize: 10, height: 20 }}
        />
        {country && (
          <Typography variant="caption" color="text.secondary">
            {country}
          </Typography>
        )}
      </Box>
      {compTypes && (
        <Typography variant="caption" color="text.secondary">
          {compTypes}
        </Typography>
      )}
      {partnership.created_at && (
        <Typography variant="caption" color="text.secondary">
          {new Date(partnership.created_at * 1000).toLocaleDateString()}
        </Typography>
      )}
    </Box>
  );
}
